import {
  EntityListItemResponse,
  EntityListItemType,
  EntityListResponse,
  EntityListSourceType,
  EntityListTypeName,
} from '@ydistri/api-sdk';
import {
  EntityListSourceTypeSummary,
  EntityListsSummary,
  EntityListTypeMap,
  ValidEntityListTypeName,
} from './entityListTypes';
import { EntityListSpecification } from './specifications/EntityListSpecification';
import { MinLayerEntityListSpecification } from './specifications/MinLayerEntityListSpecification';
import { RequestEntityListSpecification } from './specifications/RequestEntityListSpecification';
import { ProductEntityListSpecification } from './specifications/ProductEntityListSpecification';
import { SourceEntityListSpecification } from './specifications/SourceEntityListSpecification';
import { TargetEntityListSpecification } from './specifications/TargetEntityListSpecification';
import { ForcedEntityListSpecification } from './specifications/ForcedEntityListSpecification';
import { ForcedToZeroEntityListSpecification } from './specifications/ForcedToZeroEntityListSpecification';
import { getArchivedItemsCount } from '../../common/administrationItemsLib';
import { ROUTES } from '../../../../components/menu/getMenuLeftItems';
import { generatePath } from 'react-router';
import { ROOT_CATEGORY_ID } from '../../../../components/global/_constants';

export const IMPORT_STEPS = {
  // NO_IMPORT: `step0`,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- we use it like enums here
  ITEM_IMPORT: `step1`,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- we use it like enums here
  ITEM_VALIDATION: `step2`,
} as const;

/**
 * Factory for creating instances of Entity List definitions.
 */
const entityListFactory = {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- it is a correct type
  entityListFactory: {
    [EntityListTypeName.MinLayer]: () => new MinLayerEntityListSpecification(),
    [EntityListTypeName.Request]: () => new RequestEntityListSpecification(),
    [EntityListTypeName.Product]: () => new ProductEntityListSpecification(),
    [EntityListTypeName.Source]: () => new SourceEntityListSpecification(),
    [EntityListTypeName.Target]: () => new TargetEntityListSpecification(),
    [EntityListTypeName.Forced]: () => new ForcedEntityListSpecification(),
    [EntityListTypeName.ForcedToZero]: () => new ForcedToZeroEntityListSpecification(),
  } as {
    [K in keyof EntityListTypeMap]: () => EntityListSpecification<EntityListTypeMap[K]>;
  },

  entityListCache: new Map<
    keyof EntityListTypeMap,
    EntityListSpecification<EntityListTypeMap[keyof EntityListTypeMap]>
  >(),

  /**
   * Returns an instance of Entity List definition for the given type.
   * Entity list definition provides data and methods for managing the entity list.
   * @param type The type of entity list implementation to retrieve
   */
  getInstance<K extends ValidEntityListTypeName>(
    type: K,
  ): EntityListSpecification<EntityListTypeMap[K]> {
    if (this.entityListCache.has(type)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- it is a correct type
      return this.entityListCache.get(type) as EntityListSpecification<EntityListTypeMap[K]>;
    }

    const creator = this.entityListFactory[type];

    const instance = creator();
    // @ts-expect-error -- it is a correct type
    this.entityListCache.set(type, instance);
    return instance;
  },
};

/**
 * Returns an instance of Entity List implementation for the given type.
 * Entity list implementation provides data and methods for managing the entity list.
 * @param type
 */
export function getEntityListSpecification<K extends ValidEntityListTypeName>(
  type: K,
): EntityListSpecification<EntityListTypeMap[K]> {
  return entityListFactory.getInstance(type);
}

/**
 * Generates a summary of entity lists, including counts of archived items and counts by source type.
 * @param entityLists
 * @param includeArchived should archived items be included in the source type counts
 */
export const getEntityListsSummary = (
  entityLists: EntityListResponse[],
  includeArchived: boolean = false,
): EntityListsSummary => {
  const archivedItemsCount = getArchivedItemsCount(entityLists);

  const initial: EntityListSourceTypeSummary = {
    [EntityListSourceType.User]: 0,
    [EntityListSourceType.Integration]: 0,
    [EntityListSourceType.Computation]: 0,
  };

  const sourceTypeCounts = entityLists
    .filter(e => includeArchived || !e.isArchived)
    .reduce<EntityListSourceTypeSummary>(
      (acc, { entityListSourceTypeId }) => {
        acc[entityListSourceTypeId] = acc[entityListSourceTypeId] + 1;
        return acc;
      },
      { ...initial },
    );

  return { archivedItemsCount, sourceTypeCounts };
};

/**
 * Generates a link to the Detail screen for the given entity list item which can
 * be either product or sku.
 * @param item - The entity list item representing either a product or Sku
 * @param projectShortName - The project short name
 * @param slug - The category slug, defaults to root category
 * @returns string link to the detail page or undefined if not possible
 */
export const getEntityListItemDetailLink = (
  item: EntityListItemResponse,
  projectShortName?: string,
  slug: string = `${ROOT_CATEGORY_ID}`,
): string | undefined => {
  const path = generatePath(ROUTES.detail, { projectShortName, slug });

  if (item.sku) {
    return `${path}?sku=${item.sku.skuId}`;
  }

  if (item.product) {
    return `${path}?product=${item.product.id}`;
  }
};

/**
 * Checks if the given entity list item type is sku-based, meaning it can be associated with a SKU.
 * @param itemType - The entity list item type to check
 * @returns true if the item type is sku-based, false otherwise
 */
export function isSkuBasedItemType(itemType: EntityListItemType): boolean {
  return itemType === EntityListItemType.Sku || itemType === EntityListItemType.SkuQuantity;
}
